import { mulberry32 } from "./rng";

/**
 * FairCycleSelector (§3.1).
 *
 * Pure-functional API:
 *   pickNext({ allIds, remainingIds, seedStream, lastPickedId? })
 *     → { pickedId, nextRemainingIds, nextSeedStream }
 *
 * - When `remainingIds` is empty, it is refilled from `allIds` (start of a new cycle).
 *   When refilling, the player who was just picked (`lastPickedId`) is excluded
 *   from the new pool to prevent back-to-back picks across cycle boundaries
 *   (the strict §3.1 rule allows this, but it feels unfair in real play).
 * - The pick is uniformly random within the current `remainingIds` / pool.
 * - The picked id is removed from the remaining set, so every player is picked
 *   once before any player is picked twice.
 *
 * The seed stream is just an integer that we advance after each pick — this
 * keeps the function pure while letting us derive a deterministic PRNG from
 * the session seed (so reloads pick the same player as before for the same
 * action sequence).
 */
export interface FairPick {
  pickedId: string;
  nextRemainingIds: string[];
  nextSeedStream: number;
}

/**
 * Pick N helpers from the helper pool, applying the same FairCycle invariant
 * as `pickNext` but in batch. The main player (`excludeId`) is always
 * filtered out so a player never helps themselves. Refills automatically when
 * the pool runs low.
 *
 * Caps `count` at `allIds.length - 1` so we never request more helpers than
 * there are other players in the room.
 */
export interface HelperPick {
  pickedIds: string[];
  nextRemainingIds: string[];
  nextSeedStream: number;
}

export function pickHelpers(args: {
  allIds: readonly string[];
  excludeId: string;
  remainingIds: readonly string[];
  count: number;
  seedStream: number;
}): HelperPick {
  const otherIds = args.allIds.filter((id) => id !== args.excludeId);
  const cap = Math.max(0, Math.min(args.count, otherIds.length));
  if (cap === 0) {
    return {
      // Keep current main player IN remaining — they may be a helper next card.
      // Only filter them out of the "candidates for THIS card" pool.
      pickedIds: [],
      nextRemainingIds: [...args.remainingIds],
      nextSeedStream: args.seedStream,
    };
  }
  // Working pool: remaining minus the main player (can't help themselves).
  let pool = args.remainingIds.filter((id) => id !== args.excludeId);
  let didRefill = false;
  let stream = args.seedStream >>> 0;
  const picked: string[] = [];
  const pickedSet = new Set<string>();
  while (picked.length < cap) {
    if (pool.length === 0) {
      // Refill: everyone except main, minus those already picked this card.
      pool = otherIds.filter((id) => !pickedSet.has(id));
      didRefill = true;
      if (pool.length === 0) break; // safety
    }
    const rng = mulberry32(stream);
    const idx = Math.floor(rng() * pool.length);
    const id = pool[idx] as string;
    picked.push(id);
    pickedSet.add(id);
    pool.splice(idx, 1);
    stream = (stream + 0x9e3779b1) >>> 0;
  }
  // Build next remaining preserving the cycle invariant:
  //  - main player stays in remaining (they will be eligible to help others later)
  //  - picked helpers are removed (consumed this cycle)
  //  - if we refilled mid-pick, restart the cycle from "all - picked".
  const nextRemainingIds = didRefill
    ? args.allIds.filter((id) => !pickedSet.has(id))
    : args.remainingIds.filter((id) => !pickedSet.has(id));
  return { pickedIds: picked, nextRemainingIds, nextSeedStream: stream };
}

export function pickNext(args: {
  allIds: readonly string[];
  remainingIds: readonly string[];
  seedStream: number;
  /** Player who was on screen for the previous turn — excluded on refill. */
  lastPickedId?: string | null;
}): FairPick {
  if (args.allIds.length === 0) {
    throw new Error("FairCycle: empty player list");
  }
  let pool: string[];
  if (args.remainingIds.length === 0) {
    pool = [...args.allIds];
    // Avoid back-to-back same player at the cycle boundary, but only if
    // there are alternatives. With a single player (impossible per §3.6 but
    // defensive), don't filter out the only candidate.
    if (args.lastPickedId && pool.length > 1) {
      pool = pool.filter((id) => id !== args.lastPickedId);
    }
  } else {
    pool = [...args.remainingIds];
  }
  const rng = mulberry32(args.seedStream);
  const idx = Math.floor(rng() * pool.length);
  const pickedId = pool[idx] as string;
  // Build the new remaining set against the FULL allIds — losing the
  // anti-back-to-back filter doesn't change the cycle invariant.
  const fullPool =
    args.remainingIds.length === 0 ? [...args.allIds] : [...args.remainingIds];
  const removeAt = fullPool.indexOf(pickedId);
  if (removeAt >= 0) fullPool.splice(removeAt, 1);
  return {
    pickedId,
    nextRemainingIds: fullPool,
    nextSeedStream: (args.seedStream + 0x9e3779b1) >>> 0, // golden-ratio increment
  };
}
